import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { AlertCircle, BadgeCheck, Loader2, Trash2, X } from 'lucide-react';
import { getApiBase, getAuthHeaders } from '../../utils/api';

/**
 * Inline editor for a VIP service provider profile (org-scoped).
 *
 * Supports both editing an existing provider (provider.id present) and
 * creating one ahead of submitting a component (provider.id absent).
 * Calls onSaved(updatedProvider) on success and onDeleted(providerId) on delete.
 */
const EditVipProviderModal = ({ provider, onClose, onSaved, onDeleted }) => {
  const [form, setForm] = useState({
    name: '',
    website: '',
    phone: '',
    email: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    setForm({
      name: provider?.name || '',
      website: provider?.website || '',
      phone: provider?.phone || '',
      email: provider?.email || '',
      notes: provider?.notes || '',
    });
    setError('');
    setConfirmDelete(false);
  }, [provider]);

  const isNew = !provider?.id;

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim()) {
      setError('Provider name is required.');
      return;
    }
    setSaving(true);
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const payload = {
        name: form.name.trim(),
        website: form.website.trim() || null,
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
        notes: form.notes.trim() || null,
      };
      const response = isNew
        ? await axios.post(`${apiBase}/vip-service-providers`, payload, { headers })
        : await axios.put(`${apiBase}/vip-service-providers/${provider.id}`, payload, { headers });
      onSaved && onSaved(response.data);
      onClose && onClose();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Save failed';
      setError(typeof detail === 'string' ? detail : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!provider?.id) return;
    setError('');
    setDeleting(true);
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      await axios.delete(`${apiBase}/vip-service-providers/${provider.id}`, { headers });
      onDeleted && onDeleted(provider.id);
      onClose && onClose();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Delete failed';
      setError(typeof detail === 'string' ? detail : 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-ui-overlay/40 flex items-center justify-center z-[80]">
      <div className="bg-ui-surface rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-ui-line">
          <div className="flex items-center gap-2">
            <BadgeCheck className="w-5 h-5 text-ui-amber-fg" />
            <h2 className="text-lg font-semibold text-ui-ink">
              {isNew ? 'New VIP Service Provider' : 'Edit VIP Service Provider'}
            </h2>
          </div>
          <button onClick={onClose} className="text-ui-muted hover:text-ui-ink-2">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSave} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-ui-red-bg border border-ui-red-line text-ui-red-fg rounded-md text-sm flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div>
            <label className="text-sm font-semibold text-ui-ink-2">
              Company Name <span className="text-ui-red-fg">*</span>
            </label>
            <input
              type="text"
              value={form.name}
              onChange={handleChange('name')}
              required
              placeholder="e.g. Laufer"
              className="mt-1 w-full h-11 px-3 border border-ui-line rounded-lg focus:border-ui-amber-solid focus:ring-ui-amber-solid focus:ring-2 focus:ring-opacity-20 transition-all bg-ui-surface text-ui-ink"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-semibold text-ui-ink-2">Website</label>
              <input
                type="text"
                value={form.website}
                onChange={handleChange('website')}
                placeholder="laufer-vip.com"
                className="mt-1 w-full h-11 px-3 border border-ui-line rounded-lg focus:border-ui-amber-solid focus:ring-ui-amber-solid focus:ring-2 focus:ring-opacity-20 transition-all bg-ui-surface text-ui-ink"
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-ui-ink-2">24/7 Dispatch Phone</label>
              <input
                type="text"
                value={form.phone}
                onChange={handleChange('phone')}
                placeholder="+1 555 0100"
                className="mt-1 w-full h-11 px-3 border border-ui-line rounded-lg focus:border-ui-amber-solid focus:ring-ui-amber-solid focus:ring-2 focus:ring-opacity-20 transition-all bg-ui-surface text-ui-ink"
              />
            </div>
          </div>

          <div>
            <label className="text-sm font-semibold text-ui-ink-2">Email</label>
            <input
              type="email"
              value={form.email}
              onChange={handleChange('email')}
              placeholder="ops@laufer-vip.com"
              className="mt-1 w-full h-11 px-3 border border-ui-line rounded-lg focus:border-ui-amber-solid focus:ring-ui-amber-solid focus:ring-2 focus:ring-opacity-20 transition-all bg-ui-surface text-ui-ink"
            />
          </div>

          <div>
            <label className="text-sm font-semibold text-ui-ink-2">Notes</label>
            <textarea
              value={form.notes}
              onChange={handleChange('notes')}
              rows={3}
              placeholder="Account number, preferred terminals, payment details, after-hours contacts…"
              className="mt-1 w-full px-3 py-2 border border-ui-line rounded-lg focus:border-ui-amber-solid focus:ring-ui-amber-solid focus:ring-2 focus:ring-opacity-20 transition-all bg-ui-surface text-ui-ink resize-none"
            />
          </div>

          <div className="flex items-center justify-between pt-2 border-t border-ui-divider">
            {!isNew ? (
              confirmDelete ? (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-ui-red-fg">Delete this provider?</span>
                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={deleting}
                    className="px-3 py-1.5 text-sm bg-ui-red-strong text-ui-on-primary rounded-md hover:bg-ui-red-strong/90 disabled:opacity-50 inline-flex items-center gap-1"
                  >
                    {deleting && <Loader2 className="w-3 h-3 animate-spin" />}
                    Confirm
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmDelete(false)}
                    className="px-3 py-1.5 text-sm text-ui-ink-3 hover:text-ui-ink"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirmDelete(true)}
                  className="inline-flex items-center gap-1 text-sm text-ui-red-fg hover:text-ui-red-fg"
                >
                  <Trash2 className="w-4 h-4" /> Delete provider
                </button>
              )
            ) : <span />}

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm border border-ui-control rounded-md hover:bg-ui-hover"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 text-sm bg-ui-amber-strong text-ui-on-primary rounded-md hover:bg-ui-amber-strong/90 disabled:opacity-50 inline-flex items-center gap-1"
              >
                {saving && <Loader2 className="w-3 h-3 animate-spin" />}
                Save
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditVipProviderModal;
